import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  Label,
  Row,
} from "reactstrap";
import WizardError from "../auth/register/WizardError";
import NumberFormat from "react-number-format";
import Flag from "react-flags";
import WizardInput from "../auth/register/WizardInput";
import SelectProviderListItem from "../common/SelectProviderListItem";
import FullPagination from "../common/pagination/FullPagination";
import { fetchProviders } from "../../helpers/api/provider";
import { useSelector } from "react-redux";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import { Toast } from "../UI/toast/Toast";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { createVigilant, editVigilant } from "../../helpers/api/security";
import ButtonIcon from "../common/ButtonIcon";
import { cleanVePhone, getVePhoneFormat } from "../../helpers/utils";
import { faUndo, faUpload } from "@fortawesome/free-solid-svg-icons";
import { PreloadState } from "../UI/preloading/PreloadState";
import { toInteger } from 'lodash';

const CreateVigilants = () => {
  const { state } = useLocation();
  const vigilant = state ? state.vigilant : null;
  const { authId } = useParams();
  const history = useHistory();
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const {
    register,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: "false",
    shouldFocusError: true,
  });
  const [prefix, setPrefix] = useState("V");
  const [phoneDigits, setPhoneDigits] = useState(cleanVePhone(vigilant?.phone));
  const [dni, setDni] = useState("");
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [IsFromProvider, setIsFromProvider] = useState(false);
  const [dnia, setDniA] = useState(null);
  const [params] = useState({
    condominiumId: selectedCondominium,
  });

  useEffect(() => {
    if (vigilant) {
      register("firstname");
      setValue("firstname", vigilant?.firstname);
      register("lastname");
      setValue("lastname", vigilant?.lastname);
      register("dni");
      setValue("dni", vigilant?.dni);
      setDni(vigilant?.dni);
      register("phone");
      setValue("phone", vigilant?.phone);
      register("address");
      setValue("address", vigilant?.address);
    }
    // eslint-disable-next-line
  }, [vigilant]);

  
  useEffect(() => {
    if (vigilant?.dni) {
        setDniA(toInteger(vigilant?.dni.substring(1, 40)));
        setPrefix(vigilant?.dni.charAt(0))
    }
}, [vigilant?.dni])

  register("firstname");
  register("lastname");

  const {
    data,
    meta,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(fetchProviders, params);

  register("dni");

  useEffect(() => {
    if (dni) clearErrors("dni");
    else setError("dni", { message: "El DNI es requerido." });
    // eslint-disable-next-line
  }, [dni]);

  register("phone");

  const renderProvidersItems = () => {
    return data.map((provider) => {
      return (
        <SelectProviderListItem
          avatar={"https://picsum.photos/200/200"}
          onSelect={() => setSelectedProvider(provider.id)}
          selected={selectedProvider}
          id={provider.id}
          key={provider.id}
          title={provider.name}
          toDeleteId={provider.id}
          toEditId={provider.id}
          createdAt={provider.created_at}
          alternativeName="proveedor"
        />
      );
    });
  };

  const saveVigilant = async (values) => {
    if (!vigilant) {
      let obj = {
        condominium: selectedCondominium,
        ...values,
      };
      if (selectedProvider) {
        obj["provider"] = selectedProvider;
      }
      try {
        const { code } = await createVigilant(obj);
        if (code === SUCCESS_RESPONSE.CREATED) {
          Toast.fire({
            type: "success",
            title: `Vigilante registrado exitosamente.`,
          });
          history.push("/dashboard/security/vigilants");
        }
      } catch (error) {
        handleErrorMessage(error);
      }
    } else {
      try {
        const { firstname, lastname, address, phone } = values;
        let obj = { firstname, lastname, address, phone };
        const { code } = await editVigilant(authId, obj);
        if (code === SUCCESS_RESPONSE.OK) {
          Toast.fire({
            type: "success",
            title: `Vigilante actualizado exitosamente.`,
          });
          history.push("/dashboard/security/vigilants");
        }
      } catch (error) {
        handleErrorMessage(error);
      }
    }
  };

  return (
    <>
      <Card tag={Form} onSubmit={handleSubmit(saveVigilant)}>
        <PreloadState
          loading={isSubmitting}
          message="Registrando por favor espere..."
        >
          <CardHeader>
            <h5>
              {!vigilant ? "Registro de vigilante" : "Edición de vigilante"}
            </h5>
          </CardHeader>
          <CardBody className="bg-light">
            <Row form>
              <Col tag={FormGroup}>
                <Label>Nombre</Label>
                <Input
                  name="firstname"
                  className={`${
                    errors?.firstname?.message ? "border-danger" : ""
                  } form-control input-spin-none`}
                  defaultValue={vigilant?.firstname}
                  onChange={({ target }) => setValue("firstname", target.value)}
                  innerRef={register({
                    required: {
                      value: true,
                      message: "El nombre del vigilante es requerido",
                    },
                    pattern: {
                      value:
                        /^[a-zA-ZÀ-ÿ\u00f1\u00d1]+(\s*[a-zA-ZÀ-ÿ\u00f1\u00d1]*)*[a-zA-ZÀ-ÿ\u00f1\u00d1]+$/i,
                      message: "Nombre inválido.",
                    },
                  })}
                />
                {errors.firstname && (
                  <WizardError
                    error={errors.firstname.message}
                    className="fs--1 font-weight-normal d-block"
                  />
                )}
              </Col>
              <Col tag={FormGroup}>
                <Label>Apellido</Label>
                <Input
                  className={`${
                    errors?.lastname?.message ? "border-danger" : ""
                  } form-control input-spin-none`}
                  name="lastname"
                  defaultValue={vigilant?.lastname}
                  onChange={({ target }) => setValue("lastname", target.value)}
                  innerRef={register({
                    required: {
                      value: true,
                      message: "El apellido del vigilante es requerido",
                    },
                    pattern: {
                      value:
                        /^[a-zA-ZÀ-ÿ\u00f1\u00d1]+(\s*[a-zA-ZÀ-ÿ\u00f1\u00d1]*)*[a-zA-ZÀ-ÿ\u00f1\u00d1]+$/i,
                      message: "Apellido inválido.",
                    },
                  })}
                />
                {errors.lastname && (
                  <WizardError
                    error={errors.lastname.message}
                    className="fs--1 font-weight-normal d-block"
                  />
                )}
              </Col>
            </Row>
            {vigilant && (
                   <Row form>
                   <Col tag={FormGroup}>
                     <Label>DNI (Cédula de identidad o RIF)</Label>
                     <InputGroup>
                       <InputGroupAddon addonType="prepend">
                         <Input
                         value={prefix}
                           disabled
                           type="select"
                           onChange={(e) => {
                             setPrefix(e.target.value);
                           }}
                         >
                           <option value="V">V</option>
                           <option value="E">E</option>
                           <option value="J">J</option>
                         </Input>
                       </InputGroupAddon>
                       <NumberFormat
                         disabled
                         value={vigilant?.dni || undefined}
                         onValueChange={({ value }) => {
                           setValue("dni", `${prefix}${value}`);
                           setDni(value);
                         }}
                         className={`${
                           errors?.dni?.message ? "border-danger" : ""
                         } form-control input-spin-none`}
                         thousandSeparator={true}
                         getInputRef={register("dni", {
                           required: {
                             value: true,
                             message: "El DNI es obligatorio",
                           },
                         })}
                         renderText={(value, props) => (
                           <div {...props}>{value}</div>
                         )}
                       />
                     </InputGroup>
                     {errors.dni && (
                       <WizardError
                         error={errors.dni.message}
                         className="fs--1 font-weight-normal d-block"
                       />
                     )}
                   </Col>
                 </Row>
                 /*   <Row form>
                   <Col tag={FormGroup}>
                     <Label>DNI (Cédula de identidad o RIF)</Label>
                     <Input disabled defaultValue={vigilant?.dni} />
                   </Col>
                 </Row> */
            )}
            {!vigilant && (
              <Row form>
                <Col tag={FormGroup}>
                  <Label>DNI (Cédula de identidad o RIF)</Label>
                  <InputGroup>
                    <InputGroupAddon addonType="prepend">
                      <Input
                        type="select"
                        onChange={(e) => {
                          setPrefix(e.target.value);
                        }}
                      >
                        <option value="V">V</option>
                        <option value="E">E</option>
                        <option value="J">J</option>
                      </Input>
                    </InputGroupAddon>
                    <NumberFormat
                      onValueChange={({ value }) => {
                        setValue("dni", `${prefix}${value}`);
                        setDni(value);
                      }}
                      className={`${
                        errors?.dni?.message ? "border-danger" : ""
                      } form-control input-spin-none`}
                      thousandSeparator={true}
                      getInputRef={register("dni", {
                        required: {
                          value: true,
                          message: "El DNI es obligatorio",
                        },
                      })}
                      renderText={(value, props) => (
                        <div {...props}>{value}</div>
                      )}
                    />
                  </InputGroup>
                  {errors.dni && (
                    <WizardError
                      error={errors.dni.message}
                      className="fs--1 font-weight-normal d-block"
                    />
                  )}
                </Col>
              </Row>
            )}
            <Row form>
              <Col tag={FormGroup}>
                <Label>Teléfono</Label>
                <InputGroup>
                  <InputGroupAddon addonType="prepend">
                    <Flag
                      name="VE"
                      format="png"
                      pngSize={32}
                      className="mr-2"
                      shiny={false}
                      basePath="/flags"
                      alt="Venezuelan Flag"
                    />
                  </InputGroupAddon>
                  <NumberFormat
                    placeholder="04241234567 o 4241234567"
                    value={phoneDigits}
                    className="form-control input-spin-none"
                    onValueChange={({ value }) => {
                      const cleaned = cleanVePhone(value);
                      setPhoneDigits(cleaned);
                      setValue("phone", cleaned);
                    }}
                    format={getVePhoneFormat(phoneDigits)}
                  />
                </InputGroup>
                <WizardError
                  error={errors["phone"]?.message}
                  className="fs--1 font-weight-normal d-block"
                />
              </Col>
            </Row>
            <Row form>
              <Col tag={FormGroup}>
                <WizardInput
                  type="textarea"
                  label="Dirección"
                  name="address"
                  rows="4"
                  defaultValue={vigilant?.address ? vigilant?.address : ""}
                  id="address"
                  innerRef={register({
                    required: false,
                  })}
                  errors={errors}
                />
              </Col>
            </Row>
            {!vigilant && (
              <Row form>
                <Col tag={FormGroup} className="d-flex align-items-center">
                  <Label className="mb-0">¿Pertenece a un proveedor?</Label>
                  <Input
                    type="checkbox"
                    onChange={({ target }) => {
                      setIsFromProvider(target.checked);
                      if (!target.checked) {
                        setSelectedProvider(null);
                      }
                    }}
                    value={IsFromProvider}
                    className="position-relative ml-2 mr-0"
                  />
                </Col>
              </Row>
            )}
            {!vigilant && IsFromProvider && (
              <Row form>
                <Col tag={FormGroup}>
                  <Label>Seleccione un proveedor de la lista</Label>
                  {IsFromProvider && renderProvidersItems()}
                  <FullPagination
                    nextPage={nextPage}
                    prevPage={previousPage}
                    goToPage={goToPage}
                    meta={meta}
                    className={"shadow-none"}
                    posText={"proveedores"}
                    perPage={perPage}
                  />
                </Col>
              </Row>
            )}
            <Row>
              <Col className="d-flex justify-content-end">
                <ButtonIcon
                  color="falcon-default"
                  className="ml-auto px-5 mr-2"
                  icon={faUndo}
                  onClick={() => history.goBack()}
                  iconAlign="right"
                  transform="down-1 shrink-4"
                >
                  Volver
                </ButtonIcon>
                <ButtonIcon
                  data-testid="submit-button"
                  color="primary"
                  className="px-5 ml-2"
                  type="submit"
                  icon={!vigilant ? faUpload : "save"}
                  iconAlign="right"
                  /*    disabled={!isValid} */
                  transform="down-1 shrink-4"
                >
                  {!vigilant ? "Registrar" : "Guardar"}
                </ButtonIcon>
              </Col>
            </Row>
          </CardBody>
        </PreloadState>
      </Card>
    </>
  );
};

export default CreateVigilants;
